import { useQuery } from "@tanstack/react-query";
import type { OrganizationsResponse, Permission } from "@mubin/contracts";
import { api } from "./api";
import { useMe } from "./session";

/** The organization the portal works in. V1 runs one organization (C1); the first visible one is used. */
export function useOrg() {
  const { data: me } = useMe();
  const orgs = useQuery({
    queryKey: ["organizations", me?.activeRole],
    queryFn: () => api<OrganizationsResponse>("/v1/organizations"),
  });
  return orgs.data?.organizations[0] ?? null;
}

/**
 * What the active role may do (ADR 0018): its permissions, as the server reports them. UI hint only: the
 * server authorizes every request again.
 */
export function useCan() {
  const { data: me } = useMe();
  const perms = new Set<Permission>(me?.permissions ?? []);
  const has = (p: Permission) => perms.has(p);
  return {
    has,
    /** Defines the organization's roles (an administrator with full permissions). */
    fullAdmin: me?.fullAdmin ?? false,
    /** New branches are an organization administrator's (the API's branch.create). */
    createBranch: me?.activeRole === "org_admin" && has("branches.manage"),
  };
}

/** A readable random password for accounts the administration issues (ADR 0011: no SMS). */
export function suggestPassword(): string {
  const alphabet = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let body = "";
  while (body.length < 10) {
    // Rejection sampling keeps every character equally likely.
    for (const b of crypto.getRandomValues(new Uint8Array(16))) {
      if (b < 256 - (256 % alphabet.length) && body.length < 10) body += alphabet[b % alphabet.length];
    }
  }
  return `${body.slice(0, 5)}-${body.slice(5)}`;
}
